<template>
    <div v-if="deskData.length" class="gap-sm flex flex-col">
        <div v-for="desk in deskData" :key="desk.name" class="p-sm flex h-fit flex-col rounded-sm border">
            <div class="gap-sm flex">
                <button
                    class="gap-sm py-xs visible-on-hover flex w-full cursor-pointer hover:bg-(--surface-highlight)"
                    :class="{ 'border-l-2 border-(--border-focus) bg-(--surface-highlight)': desk.isExpanded }"
                    @click="(e) => toggleDesk(e, desk)"
                >
                    <i class="i-dropdown-arrow transition-transform" :class="{ 'rotate-180': desk.isExpanded }"></i>
                    <p class="bold">{{ desk.name }}</p>
                </button>
                <input
                    v-if="editable"
                    :id="desk.service_desk_id.toString()"
                    v-model="desk.isChecked"
                    type="checkbox"
                    :tabindex="0"
                    class="parent focus:border-(--border-focus) focus:ring-2 focus:ring-(--border-focus) focus:outline-hidden"
                    :v-if="editable"
                    :disabled="!!associatedDesks?.length || locked || isDeskLocked(desk.service_desk_id)"
                    @click="parentToChildren(desk)"
                    @keydown.enter.prevent="parentToChildren(desk)"
                />
            </div>

            <div v-if="desk.isExpanded" class="gap-sm ml-8 flex flex-col py-4 pl-4">
                <div v-for="req in desk.request_types" :key="req.name" class="gap-sm flex h-fit w-full justify-between">
                    <p>{{ req.name }}</p>
                    <input
                        :id="req.service_desk_id.toString()"
                        v-model="req.isChecked"
                        :v-if="editable"
                        type="checkbox"
                        :tabindex="0"
                        class="child focus:border-(--border-focus) focus:ring-2 focus:ring-(--border-focus) focus:outline-hidden"
                        :disabled="
                            !!associatedDesks?.length ||
                            locked ||
                            isDeskLocked(desk.service_desk_id) ||
                            isRequestTypeLocked(desk.service_desk_id, req.request_type_id)
                        "
                        @click="childToParent(desk, req)"
                        @keydown.enter.prevent="childToParent(desk, req)"
                    />
                </div>
            </div>
        </div>
    </div>
    <div v-else class="gap-sm flex flex-col">
        <div v-for="desk in readOnlyDesks" :key="desk.name" class="p-sm flex h-fit flex-col rounded-sm border">
            <div class="gap-sm py-xs visible-on-hover flex w-full cursor-pointer">
                <p class="bold pl-6">{{ desk.name }}</p>
            </div>

            <div class="gap-sm ml-8 flex flex-col py-4 pl-4">
                <div v-for="req in desk.request_types" :key="req.name" class="gap-sm flex h-fit w-full justify-between">
                    <p>{{ req.name }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type {
    AdminOrgInfoBody,
    AdminServiceDeskData,
    AdminServiceDeskInfo,
    AdminServiceDeskReadOnly,
} from '@/stores/store-interfaces';
import { ref, watch, type PropType } from 'vue';

const props = defineProps({
    desks: Array as PropType<AdminServiceDeskInfo[]>,
    associatedDesks: Array as PropType<AdminOrgInfoBody['service_desks']>,
    chosenServiceDesks: Array as PropType<AdminOrgInfoBody['service_desks']>,
    lockedServiceDesks: Array as PropType<AdminOrgInfoBody['service_desks']>,
    readOnlyDesks: Array as PropType<AdminServiceDeskReadOnly[]>,
    locked: Boolean,
});

const deskData = ref<AdminServiceDeskData[]>([]);
const editable = !props.readOnlyDesks?.length;

const toggleDesk = (e: Event, desk: AdminServiceDeskData) => {
    e.preventDefault();
    desk.isExpanded = !desk.isExpanded;
};

function isDeskLocked(serviceDeskId: number) {
    return !!props.lockedServiceDesks?.some((desk) => desk.service_desk_id === serviceDeskId);
}

function isRequestTypeLocked(serviceDeskId: number, requestTypeId: number) {
    return !!props.lockedServiceDesks
        ?.find((desk) => desk.service_desk_id === serviceDeskId)
        ?.request_types.some((requestType) => requestType.request_type_id === requestTypeId);
}

const childToParent = (parent: AdminServiceDeskData, child: AdminServiceDeskData['request_types'][number]) => {
    if (isDeskLocked(parent.service_desk_id) || isRequestTypeLocked(parent.service_desk_id, child.request_type_id)) {
        return;
    }

    child.isChecked = !child.isChecked;
    if (child.isChecked) {
        parent.isChecked = true;
    }

    sendData();
};

const parentToChildren = (desk: AdminServiceDeskData) => {
    if (isDeskLocked(desk.service_desk_id)) {
        return;
    }

    desk.isChecked = !desk.isChecked;
    if (!desk.isChecked) {
        desk.request_types.forEach((req) => {
            req.isChecked = false;
        });
    }
    if (desk.isChecked && !desk.isExpanded) {
        desk.isExpanded = true;
    }

    sendData();
};

watch(
    () => props.desks,
    (value) => {
        if (value)
            deskData.value = value.map((desk) => {
                const updatedRequestTypes = desk.request_types.map((req) => {
                    return {
                        ...req,
                        isChecked: false,
                    };
                });
                return {
                    ...desk,
                    request_types: updatedRequestTypes,
                    isExpanded: false,
                    isChecked: false,
                };
            });

        // Apply selections based on associatedDesks if read-only context
        if (props.associatedDesks?.length) {
            deskData.value.forEach((desk) => {
                const associatedDesk = props.associatedDesks?.find((d) => d.service_desk_id === desk.service_desk_id);
                if (associatedDesk) {
                    desk.isChecked = true;
                    desk.request_types.forEach((req) => {
                        const associatedReq = associatedDesk.request_types.find(
                            (r) => r.request_type_id === req.request_type_id,
                        );
                        if (associatedReq) {
                            req.isChecked = true;
                        }
                    });
                }
            });
        } else if (props.chosenServiceDesks?.length) {
            deskData.value.forEach((desk) => {
                const chosenDesk = props.chosenServiceDesks?.find((d) => d.service_desk_id === desk.service_desk_id);
                if (chosenDesk) {
                    desk.isChecked = true;
                    desk.request_types.forEach((req) => {
                        const chosenReq = chosenDesk.request_types.find(
                            (r) => r.request_type_id === req.request_type_id,
                        );
                        if (chosenReq) {
                            req.isChecked = true;
                        }
                    });
                } else {
                    desk.isChecked = false;
                    desk.request_types.forEach((req) => {
                        req.isChecked = false;
                    });
                }
            });
        }
    },
    { immediate: true },
);

// Keep deskData in sync if chosenServiceDesks changes (edit mode context)
watch(
    () => props.chosenServiceDesks,
    (newVal) => {
        if (!props.associatedDesks?.length) {
            deskData.value.forEach((desk) => {
                const filteredNewVal = Array.isArray(newVal) ? newVal.filter(Boolean) : [];
                const chosenDesk = filteredNewVal.find((d) => d.service_desk_id === desk.service_desk_id);
                if (chosenDesk) {
                    desk.isChecked = true;
                    desk.request_types.forEach((req) => {
                        const chosenReq = chosenDesk.request_types.find(
                            (r) => r.request_type_id === req.request_type_id,
                        );
                        req.isChecked = !!chosenReq;
                    });
                } else {
                    desk.isChecked = false;
                    desk.request_types.forEach((req) => {
                        req.isChecked = false;
                    });
                }
            });
        }
    },
    { deep: true },
);

const emit = defineEmits(['update']);
const sendData = () => {
    const sendData = deskData.value.filter((desk) => desk.isChecked).map((desk) => ({
        service_desk_id: desk.service_desk_id,
        request_types: desk.request_types
            .filter((req) => req.isChecked)
            .map((req) => ({ request_type_id: req.request_type_id })),
    }));

    emit('update', sendData);
};
</script>
